import { Controller } from "@hotwired/stimulus"

// Content-safety regexes — MUST match the server-side patterns in
// app/models/product.rb (TITLE_ALLOWED_CHARS, URL_PATTERN, EMAIL_PATTERN,
// PHONE_PATTERN, REPEATED_CHAR_PATTERN). If you change one side, change
// the other. Client-side runs first (blocks on input/focusout/submit),
// server-side is the authoritative gate.
const TITLE_ALLOWED_CHARS = /^[\p{L}\p{M}\p{N}\s\-'&.,%()\/]+$/u // \p{M}: Thai vowel/tone marks are combining marks, not letters
const URL_PATTERN = /(https?:\/\/|www\.[\w-]+|\b[\w-]+\.(com|gr|net|org|io|xyz|shop|store|co|eu|uk|de|fr|it|es)\b)/i
const EMAIL_PATTERN = /[\w.+-]+@[\w-]+\.[\w.-]+/
const PHONE_PATTERN = /(\+?30\s*)?\b6\d{2}[\s.-]*\d{3}[\s.-]*\d{4}\b|\+357\s*\d{7,}|\+\d{8,}/
const REPEATED_CHAR_PATTERN = /(.)\1{4,}/u
const LETTER_COUNT_MIN = 2

export default class extends Controller {
  static targets = [
    "titleInput",
    "descriptionInput",
    "categoryInput",
    "priceInput",
    "unitInput",
    "locationInput",
    "latitudeInput",
    "longitudeInput"
  ]

  static values = {
    translations: Object
  }

  connect() {
    this.setupValidation()
  }

  // ==========================================
  // SETUP
  // ==========================================

  setupValidation() {
    const form = this.element.closest('form') || this.element.querySelector('form')
    if (form) {
      form.setAttribute('novalidate', 'novalidate')
      form.addEventListener('submit', (e) => this.validateOnSubmit(e))
    }
    
    // Listen for location-selected event from Mapbox controller
    this.element.addEventListener('location-selected', () => {
      this.validateLocation()
    })
  }

  // ==========================================
  // SUBMIT VALIDATION
  // ==========================================

  validateOnSubmit(event) {
    const errors = this.validateAllFields()

    if (errors.length > 0) {
      event.preventDefault()
      const firstErrorField = this.element.querySelector('.border-red-500, .border-red-600')
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' })
        firstErrorField.focus()
      }
      return false
    }
    return true
  }

  validateAllFields() {
    const errors = []

    if (this.hasTitleInputTarget) {
      const error = this.validateTitle()
      if (error) errors.push(error)
    }

    if (this.hasDescriptionInputTarget) {
      const error = this.validateDescription()
      if (error) errors.push(error)
    }

    if (this.hasCategoryInputTarget) {
      const error = this.validateCategory()
      if (error) errors.push(error)
    }

    if (this.hasPriceInputTarget) {
      const error = this.validatePrice()
      if (error) errors.push(error)
    }

    if (this.hasUnitInputTarget) {
      const error = this.validateUnit()
      if (error) errors.push(error)
    }

    if (this.hasLocationInputTarget) {
      const error = this.validateLocation()
      if (error) errors.push(error)
    }

    return errors
  }

  // ==========================================
  // INDIVIDUAL FIELD VALIDATORS
  // ==========================================

  // Title validation
  validateTitleOnInput(event) {
    this.validateTitle()
  }

  validateTitle() {
    if (!this.hasTitleInputTarget) return null

    const field = this.titleInputTarget
    const value = field.value.trim()
    const t = this.translationsValue?.product?.title || {}

    if (!value) {
      return this.showFieldError(field, t.required || 'Please enter a product title')
    }

    if (value.length < 3) {
      return this.showFieldError(field, t.too_short || 'Title must be at least 3 characters')
    }

    if (value.length > 100) {
      return this.showFieldError(field, t.too_long || 'Title must be no more than 100 characters')
    }

    if (EMAIL_PATTERN.test(value)) {
      return this.showFieldError(field, t.no_email || 'Product name cannot contain an email address')
    }

    if (URL_PATTERN.test(value)) {
      return this.showFieldError(field, t.no_url || 'Product name cannot contain links or web addresses')
    }

    if (!TITLE_ALLOWED_CHARS.test(value)) {
      return this.showFieldError(field, t.invalid_chars || 'Product name can only contain letters, numbers, spaces, and basic punctuation')
    }

    if (REPEATED_CHAR_PATTERN.test(value)) {
      return this.showFieldError(field, t.repeated_chars || 'Please avoid repeating the same character many times')
    }

    const letterCount = (value.match(/\p{L}/gu) || []).length
    if (letterCount < LETTER_COUNT_MIN) {
      return this.showFieldError(field, t.no_gibberish || 'Please enter a real product name')
    }

    this.showFieldSuccess(field)
    return null
  }

  // Description validation — optional field. Only checks content when
  // present (URLs, phone numbers, keyboard-mashing). If empty, no error.
  validateDescriptionOnInput(event) {
    this.validateDescription()
  }

  validateDescription() {
    if (!this.hasDescriptionInputTarget) return null

    const field = this.descriptionInputTarget
    const value = field.value.trim()
    const t = this.translationsValue?.product?.description || {}

    if (!value) {
      // Optional field — clear any prior error state, don't show success either.
      this.clearFieldValidation(field)
      return null
    }

    if (value.length > 2000) {
      return this.showFieldError(field, t.too_long || 'Description cannot exceed 2000 characters')
    }

    if (URL_PATTERN.test(value)) {
      return this.showFieldError(field, t.no_url || 'Description cannot contain links or web addresses')
    }

    if (PHONE_PATTERN.test(value)) {
      return this.showFieldError(field, t.no_phone || 'Description cannot contain phone numbers')
    }

    if (REPEATED_CHAR_PATTERN.test(value)) {
      return this.showFieldError(field, t.repeated_chars || 'Please avoid repeating the same character many times')
    }

    this.showFieldSuccess(field)
    return null
  }

  // Category validation
  validateCategoryOnInput(event) {
    this.validateCategory()
  }

  validateCategory() {
    if (!this.hasCategoryInputTarget) return null

    const field = this.categoryInputTarget
    const value = field.value
    const t = this.translationsValue?.product?.category || {}

    if (!value) {
      return this.showFieldError(field, t.required || 'Please select a category')
    }

    this.showFieldSuccess(field)
    return null
  }

  // Price validation
  validatePriceOnInput(event) {
    this.validatePrice()
  }

  validatePrice() {
    if (!this.hasPriceInputTarget) return null

    const field = this.priceInputTarget
    const value = field.value.trim()
    const t = this.translationsValue?.product?.price || {}

    if (!value) {
      return this.showFieldError(field, t.required || 'Please enter a price')
    }

    const num = parseFloat(value)
    if (isNaN(num)) {
      return this.showFieldError(field, t.invalid || 'Please enter a valid number')
    }

    if (num <= 0) {
      return this.showFieldError(field, t.min || 'Price must be greater than 0')
    }

    this.showFieldSuccess(field)
    return null
  }

  // Unit validation
  validateUnitOnInput(event) {
    this.validateUnit()
  }

  validateUnit() {
    if (!this.hasUnitInputTarget) return null

    const field = this.unitInputTarget
    const value = field.value
    const t = this.translationsValue?.product?.unit || {}

    if (!value) {
      return this.showFieldError(field, t.required || 'Please select a unit')
    }

    this.showFieldSuccess(field)
    return null
  }

  // Location validation
  validateLocationOnInput(event) {
    this.validateLocation()
  }

  validateLocation() {
    if (!this.hasLocationInputTarget) return null

    const field = this.locationInputTarget
    const value = field.value.trim()
    const t = this.translationsValue?.product?.location || {}

    if (!value) {
      return this.showFieldError(field, t.required || 'Please select a location from the suggestions')
    }

    // If we have latitude/longitude targets (full Mapbox form), check they're populated
    if (this.hasLatitudeInputTarget && this.hasLongitudeInputTarget) {
      const hasCoordinates = this.latitudeInputTarget.value && this.longitudeInputTarget.value
      if (!hasCoordinates) {
        return this.showFieldError(field, t.required || 'Please select a location from the suggestions')
      }
    }

    this.showFieldSuccess(field)
    return null
  }

  // ==========================================
  // UI HELPER METHODS
  // ==========================================

  showFieldError(field, message) {
    field.classList.remove('border-green-500', 'border-green-600', 'border-gray-300')
    field.classList.add('border-red-500')

    const errorId = `${field.id || field.name}-error`
    let errorEl = document.getElementById(errorId)

    if (!errorEl) {
      errorEl = document.createElement('p')
      errorEl.id = errorId
      errorEl.className = 'text-sm text-red-600 mt-1.5 flex items-center gap-1 validation-error'
      errorEl.innerHTML = `<i class="fas fa-exclamation-circle"></i><span></span>`

      const container = field.closest('.relative') || field.parentElement
      container.appendChild(errorEl)
    }

    errorEl.querySelector('span').textContent = message
    errorEl.classList.remove('hidden')

    // Hide help text
    const helpText = field.parentElement.querySelector('.text-accent-green:not(.validation-error), .opacity-60:not(.validation-error)')
    if (helpText && !helpText.closest('.card')) helpText.classList.add('hidden')

    return message
  }

  showFieldSuccess(field) {
    field.classList.remove('border-red-500', 'border-red-600', 'border-gray-300')
    field.classList.add('border-green-500')

    const errorId = `${field.id || field.name}-error`
    const errorEl = document.getElementById(errorId)
    if (errorEl) errorEl.classList.add('hidden')

    // Show help text
    const helpText = field.parentElement.querySelector('.opacity-60.hidden:not(.validation-error)')
    if (helpText) helpText.classList.remove('hidden')
  }

  clearFieldValidation(field) {
    field.classList.remove('border-red-500', 'border-red-600', 'border-green-500', 'border-green-600')
    field.classList.add('border-gray-300')

    const errorId = `${field.id || field.name}-error`
    const errorEl = document.getElementById(errorId)
    if (errorEl) errorEl.remove()
  }

  // ==========================================
  // MODAL SUCCESS HANDLER (legacy)
  // ==========================================

  handleSuccess(event) {
    if (event.detail.success) {
      const modalController = this.application.getControllerForElementAndIdentifier(
        document.querySelector('[data-controller="product-modal"]'),
        'product-modal'
      )
      
      if (modalController) {
        modalController.close()
      }
      
      setTimeout(() => {
        window.location.reload()
      }, 300)
    }
  }
}